import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { browserLocalPersistence, EmailAuthProvider, onAuthStateChanged, reauthenticateWithCredential, setPersistence, signInWithEmailAndPassword, signOut, updatePassword } from 'firebase/auth';
import type { User } from 'firebase/auth';
import { auth, authReady, debugErrorMessage, firebaseErrorCode, firebaseErrorMessage, logAuthState, logFirebaseError } from '../lib/firebase';
import { createCaseDoc, deleteCaseDoc, subscribeCases, updateCaseDoc } from '../services/casesService';
import { createCustomerDoc, deleteCustomerDoc, subscribeCustomers, updateCustomerDoc } from '../services/customersService';
import { deleteEnquiryDoc, submitEnquiry, subscribeEnquiries, updateEnquiryDoc } from '../services/enquiriesService';
import { createQuoteDoc, deleteQuoteDoc, reserveQuoteNumber, subscribeQuotes, updateQuoteDoc } from '../services/quotesService';
import { createInvoiceDoc, deleteInvoiceDoc, reserveInvoiceNumber, subscribeInvoices, updateInvoiceDoc } from '../services/invoicesService';
import { defaultDueDate, invoiceLinesFromQuote, invoiceTotals, today as invoiceToday } from '../utils/invoice';
import type { Invoice, InvoiceStatus } from '../types/invoice';
import { sendQuoteEmail } from '../services/emailService';
import { calculateTotals, defaultQuoteTexts, defaultValidUntil, today } from '../utils/quote';
import { stageFolder, stagePaths } from '../utils/photoStage';
import { quoteFileName, quotePdfBase64 } from '../utils/quotePdf';
import type { Quote, QuoteInput, QuoteStatus } from '../types/quote';
import { defaultSettings, saveSettingsToFirestore, subscribeSettings } from '../services/settingsService';
import { deleteImage, uploadImages } from '../services/imageService';
import type { AdminSettings, Case, CaseStatus, Customer, Enquiry, EnquiryInput, NewCaseInput, PhotoStage } from '../types/admin';
export type PasswordChangeResult = 'ok' | 'wrong-current' | 'mismatch' | 'too-short' | 'failed';
interface Result {
  ok: boolean;
  error?: string;
  /** Rå Firebase-fejlkode, fx "auth/invalid-credential" — bruges til fejlsøgning. */
  code?: string;
}
interface EnquiryResult extends Result {
  /** Dokument-id'et — genbruges ved nyt forsøg, så der ikke opstår dubletter. */
  draftId?: string;
}
interface AdminData {
  user: User | null;
  loggedIn: boolean;
  authLoading: boolean;
  login: (email: string, password: string) => Promise<Result>;
  logout: () => Promise<void>;
  changePassword: (current: string, next: string, confirm: string) => Promise<PasswordChangeResult>;
  settings: AdminSettings;
  settingsLoading: boolean;
  saveSettings: (next: AdminSettings) => Promise<Result>;
  cases: Case[];
  customers: Customer[];
  enquiries: Enquiry[];
  quotes: Quote[];
  invoices: Invoice[];
  dataLoading: boolean;
  dataError: string;
  /** Udfyldt hvis Firestore Rules afviser bestemte samlinger, fx "quotes". */
  rulesError: string;
  /** Seneste skrivefejl med fejlkode — kan læses umiddelbart efter et kald. */
  lastWriteError: () => string;
  customerById: (id: string) => Customer | undefined;
  casesForCustomer: (customerId: string) => Case[];
  quotesForCustomer: (customerId: string) => Quote[];
  invoicesForCustomer: (customerId: string) => Invoice[];
  invoiceForCase: (caseId: string) => Invoice | undefined;
  createInvoiceFromCase: (caseItem: Case) => Promise<Invoice | null>;
  updateInvoice: (id: string, patch: Partial<Invoice>) => Promise<Result>;
  setInvoiceStatus: (id: string, status: InvoiceStatus) => Promise<Result>;
  deleteInvoice: (id: string) => Promise<Result>;
  createQuote: (input: QuoteInput) => Promise<Quote | null>;
  updateQuote: (id: string, patch: Partial<Quote>) => Promise<Result>;
  setQuoteStatus: (id: string, status: QuoteStatus) => Promise<Result>;
  duplicateQuote: (quote: Quote) => Promise<Quote | null>;
  sendQuoteByEmail: (quote: Quote, email: {
    to: string;
    subject: string;
    message: string;
  }) => Promise<Result>;
  deleteQuote: (id: string) => Promise<Result>;
  createCaseFromQuote: (quote: Quote) => Promise<Case | null>;
  createQuoteFromEnquiry: (enquiry: Enquiry) => Promise<Quote | null>;
  createCase: (input: NewCaseInput) => Promise<Case | null>;
  updateCase: (id: string, patch: Partial<Case>) => Promise<Result>;
  deleteCase: (id: string) => Promise<Result>;
  setStatus: (id: string, status: CaseStatus) => Promise<Result>;
  addNote: (id: string, text: string) => Promise<Result>;
  deleteNote: (caseId: string, noteId: string) => Promise<Result>;
  addPhotos: (caseId: string, files: File[], stage: PhotoStage) => Promise<Result>;
  deletePhoto: (caseId: string, photoId: string) => Promise<Result>;
  createCustomer: (customer: Omit<Customer, 'id' | 'createdAt'>) => Promise<Customer | null>;
  updateCustomer: (id: string, patch: Partial<Customer>) => Promise<Result>;
  deleteCustomer: (id: string) => Promise<Result>;
  createEnquiry: (input: EnquiryInput) => Promise<EnquiryResult>;
  acceptEnquiry: (id: string) => Promise<Result>;
  deleteEnquiry: (enquiry: Enquiry) => Promise<Result>;
  createCaseFromEnquiry: (enquiry: Enquiry) => Promise<Case | null>;
}
const AdminDataContext = createContext<AdminData | null>(null);
function uid(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}
function nextNumber(cases: Case[]): string {
  const highest = cases.reduce((max, item) => Math.max(max, Number(item.number) || 0), 0);
  return String(highest + 1).padStart(4, '0');
}
export function AdminDataProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [settings, setSettings] = useState<AdminSettings>(defaultSettings);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [cases, setCases] = useState<Case[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState('');
  /** Samlinger som Firestore Rules afviser — vises samlet, ikke som generel loginfejl. */
  const [deniedCollections, setDeniedCollections] = useState<string[]>([]);
  /** Seneste skrivefejl, tilgængelig med det samme (state opdateres først næste render). */
  const writeError = useRef('');
  const reportWriteError = useCallback((context: string, error: unknown): string => {
    logFirebaseError(context, error);
    const message = debugErrorMessage(context, error);
    writeError.current = message;
    setDataError(message);
    return message;
  }, []);

  /*
   * Login-session. Persistensen (browserLocalPersistence) sættes i lib/firebase.ts,
   * og vi venter på den, før sessionen aflæses — så en gemt session altid
   * gendannes, og login-siden aldrig vises et kort øjeblik ved refresh.
   */
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    void authReady.then(() => {
      if (cancelled) return;
      unsubscribe = onAuthStateChanged(auth, current => {
        setUser(current);
        setAuthLoading(false);
        logAuthState('onAuthStateChanged');
      });
    });
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  /* Firmaoplysninger — læses af alle besøgende */
  useEffect(() => {
    return subscribeSettings(next => {
      setSettings(next);
      setSettingsLoading(false);
    }, error => {
      logFirebaseError('onSnapshot settings/company', error);
      setSettingsLoading(false);
    });
  }, []);

  /*
   * Sager, kunder, henvendelser og tilbud — først når Firebase Auth er færdig
   * med at afgøre login-status, og kun for en indlogget administrator.
   * Hver lytter fejlhåndteres for sig, så én afvist samling ikke skjuler resten,
   * og fejlkoden logges sammen med navnet på den samling der fejler.
   */
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setCases([]);
      setCustomers([]);
      setEnquiries([]);
      setQuotes([]);
      setInvoices([]);
      setDataLoading(false);
      setDeniedCollections([]);
      setDataError('');
      return;
    }
    setDataLoading(true);
    setDeniedCollections([]);
    setDataError('');
    const handleError = (collection: string) => (error: unknown) => {
      logFirebaseError(`onSnapshot ${collection}`, error);
      setDataLoading(false);

      // Sker typisk kortvarigt under log ud — så er det ikke en reel adgangsfejl.
      if (!auth.currentUser) return;
      if (firebaseErrorCode(error) === 'permission-denied') {
        setDeniedCollections(current => current.includes(collection) ? current : [...current, collection]);
        return;
      }
      setDataError(firebaseErrorMessage(error));
    };
    const unsubCases = subscribeCases(next => {
      setCases(next);
      setDataLoading(false);
    }, handleError('cases'));
    const unsubCustomers = subscribeCustomers(next => setCustomers(next), handleError('customers'));
    const unsubEnquiries = subscribeEnquiries(next => setEnquiries(next), handleError('enquiries'));
    const unsubQuotes = subscribeQuotes(next => setQuotes(next), handleError('quotes'));
    const unsubInvoices = subscribeInvoices(next => setInvoices(next), handleError('invoices'));
    return () => {
      unsubCases();
      unsubCustomers();
      unsubEnquiries();
      unsubQuotes();
      unsubInvoices();
    };
  }, [user, authLoading]);
  const login = useCallback(async (email: string, password: string): Promise<Result> => {
    try {
      // Sessionen skal huskes i browseren, også efter at vinduet lukkes.
      await setPersistence(auth, browserLocalPersistence);
      await signInWithEmailAndPassword(auth, email.trim(), password);
      return {
        ok: true
      };
    } catch (error) {
      logFirebaseError('signInWithEmailAndPassword', error);
      return {
        ok: false,
        error: firebaseErrorMessage(error),
        code: firebaseErrorCode(error)
      };
    }
  }, []);
  const logout = useCallback(async () => {
    await signOut(auth);
  }, []);
  const changePassword = useCallback(async (current: string, next: string, confirm: string): Promise<PasswordChangeResult> => {
    const currentUser = auth.currentUser;
    if (!currentUser?.email) return 'failed';
    if (next.length < 8) return 'too-short';
    if (next !== confirm) return 'mismatch';
    try {
      await reauthenticateWithCredential(currentUser, EmailAuthProvider.credential(currentUser.email, current));
    } catch (error) {
      logFirebaseError('reauthenticateWithCredential', error);
      return 'wrong-current';
    }
    try {
      await updatePassword(currentUser, next);
      return 'ok';
    } catch (error) {
      logFirebaseError('updatePassword', error);
      return 'failed';
    }
  }, []);
  const saveSettings = useCallback(async (next: AdminSettings): Promise<Result> => {
    try {
      await saveSettingsToFirestore(next);
      return {
        ok: true
      };
    } catch (error) {
      return {
        ok: false,
        error: firebaseErrorMessage(error)
      };
    }
  }, []);
  const customerById = useCallback((id: string) => customers.find(customer => customer.id === id), [customers]);
  const casesForCustomer = useCallback((customerId: string) => cases.filter(item => item.customerId === customerId), [cases]);
  const quotesForCustomer = useCallback((customerId: string) => quotes.filter(quote => quote.customerId === customerId), [quotes]);
  const createCustomer = useCallback(async (customer: Omit<Customer, 'id' | 'createdAt'>) => {
    try {
      const id = await createCustomerDoc(customer);
      return {
        ...customer,
        id,
        createdAt: new Date().toISOString()
      };
    } catch (error) {
      setDataError(firebaseErrorMessage(error));
      return null;
    }
  }, []);
  const updateCustomer = useCallback(async (id: string, patch: Partial<Customer>): Promise<Result> => {
    try {
      await updateCustomerDoc(id, patch);
      return {
        ok: true
      };
    } catch (error) {
      return {
        ok: false,
        error: firebaseErrorMessage(error)
      };
    }
  }, []);
  const deleteCustomer = useCallback(async (id: string): Promise<Result> => {
    try {
      await deleteCustomerDoc(id);
      await Promise.all(cases.filter(item => item.customerId === id).map(item => deleteCaseDoc(item.id)));
      return {
        ok: true
      };
    } catch (error) {
      return {
        ok: false,
        error: firebaseErrorMessage(error)
      };
    }
  }, [cases]);
  const createCase = useCallback(async (input: NewCaseInput): Promise<Case | null> => {
    try {
      let customerId = input.customerId;
      let customerName = customers.find(entry => entry.id === customerId)?.name ?? '';
      if (input.newCustomer) {
        customerId = await createCustomerDoc({
          ...input.newCustomer,
          notes: ''
        });
        customerName = input.newCustomer.name;
      }
      const payload = {
        number: nextNumber(cases),
        customerId,
        customerName,
        title: input.title,
        address: input.address,
        description: input.description,
        category: input.category,
        priceModel: input.priceModel,
        hourlyRate: input.hourlyRate,
        fixedPrice: input.fixedPrice,
        estimate: input.fixedPrice || '',
        finalAmount: '',
        plannedDate: input.plannedDate,
        status: input.status,
        internalNotes: input.internalNote ? [{
          id: uid('note'),
          text: input.internalNote,
          createdAt: new Date().toISOString()
        }] : [],
        photos: [],
        fromWebsite: false
      };
      const id = await createCaseDoc(payload);
      const files = input.photos.map(photo => photo.file).filter(Boolean) as File[];
      if (files.length > 0) {
        // cases/{caseId}/before/{filnavn}
        const uploaded = await uploadImages(files, `cases/${id}/${stageFolder['Før']}`, {
          resolveUrls: true,
          requireAuth: true
        });
        const photos = uploaded.map(image => ({
          id: uid('photo'),
          stage: 'Før' as PhotoStage,
          ...image
        }));
        await updateCaseDoc(id, {
          photos,
          ...stagePaths(photos)
        });
      }
      const now = new Date().toISOString();
      return {
        ...payload,
        id,
        createdAt: now,
        updatedAt: now
      };
    } catch (error) {
      setDataError(firebaseErrorMessage(error));
      return null;
    }
  }, [cases, customers]);
  const updateCase = useCallback(async (id: string, patch: Partial<Case>): Promise<Result> => {
    try {
      await updateCaseDoc(id, patch);
      return {
        ok: true
      };
    } catch (error) {
      return {
        ok: false,
        error: firebaseErrorMessage(error)
      };
    }
  }, []);
  const deleteCase = useCallback(async (id: string): Promise<Result> => {
    try {
      const target = cases.find(item => item.id === id);
      await Promise.all((target?.photos ?? []).map(photo => deleteImage(photo.path).catch(() => null)));
      await deleteCaseDoc(id);
      return {
        ok: true
      };
    } catch (error) {
      return {
        ok: false,
        error: firebaseErrorMessage(error)
      };
    }
  }, [cases]);
  const setStatus = useCallback((id: string, status: CaseStatus) => updateCase(id, {
    status
  }), [updateCase]);
  const addNote = useCallback(async (id: string, text: string): Promise<Result> => {
    const target = cases.find(item => item.id === id);
    if (!target) return {
      ok: false,
      error: 'Sagen blev ikke fundet.'
    };
    return updateCase(id, {
      internalNotes: [{
        id: uid('note'),
        text,
        createdAt: new Date().toISOString()
      }, ...target.internalNotes]
    });
  }, [cases, updateCase]);
  const deleteNote = useCallback(async (caseId: string, noteId: string): Promise<Result> => {
    const target = cases.find(item => item.id === caseId);
    if (!target) return {
      ok: false,
      error: 'Sagen blev ikke fundet.'
    };
    return updateCase(caseId, {
      internalNotes: target.internalNotes.filter(note => note.id !== noteId)
    });
  }, [cases, updateCase]);
  const addPhotos = useCallback(async (caseId: string, files: File[], stage: PhotoStage): Promise<Result> => {
    const target = cases.find(item => item.id === caseId);
    if (!target) return {
      ok: false,
      error: 'Sagen blev ikke fundet.'
    };
    try {
      // Præcis sti som i Storage-reglerne: cases/{caseId}/before|during|after/{filnavn}
      const uploaded = await uploadImages(files, `cases/${caseId}/${stageFolder[stage]}`, {
        resolveUrls: true,
        requireAuth: true
      });
      const photos = [...target.photos, ...uploaded.map(image => ({
        id: uid('photo'),
        stage,
        ...image
      }))];
      return updateCase(caseId, {
        photos,
        ...stagePaths(photos)
      });
    } catch (error) {
      // Fejlkoden vises i UI under fejlsøgning — adminen logges aldrig ud.
      logFirebaseError('addPhotos', error);
      const code = firebaseErrorCode(error);
      const where = `cases/${caseId}/${stageFolder[stage]}/`;
      if (code === 'auth/no-session') {
        return {
          ok: false,
          error: 'Sessionen indlæses stadig. Vent et øjeblik, og prøv upload igen.'
        };
      }
      return {
        ok: false,
        error: `${debugErrorMessage('upload', error)} · sti: ${where} · uid: ${auth.currentUser?.uid ?? 'ingen'}`
      };
    }
  }, [cases, updateCase]);
  const deletePhoto = useCallback(async (caseId: string, photoId: string): Promise<Result> => {
    const target = cases.find(item => item.id === caseId);
    if (!target) return {
      ok: false,
      error: 'Sagen blev ikke fundet.'
    };
    const photo = target.photos.find(entry => entry.id === photoId);
    if (photo?.path) await deleteImage(photo.path).catch(() => null);
    const photos = target.photos.filter(entry => entry.id !== photoId);
    return updateCase(caseId, {
      photos,
      ...stagePaths(photos)
    });
  }, [cases, updateCase]);
  const createQuote = useCallback(async (input: QuoteInput): Promise<Quote | null> => {
    try {
      let customerId = input.customerId;
      let customer = customers.find(entry => entry.id === customerId);
      if (input.newCustomer) {
        customerId = await createCustomerDoc({
          ...input.newCustomer,
          notes: ''
        });
        customer = {
          ...input.newCustomer,
          notes: '',
          id: customerId,
          createdAt: ''
        };
      }
      const totals = calculateTotals(input.lines);
      const quoteNumber = await reserveQuoteNumber(quotes.map(entry => entry.quoteNumber));
      const payload: Omit<Quote, 'id' | 'createdAt' | 'updatedAt'> = {
        quoteNumber,
        customerId,
        customerName: customer?.name ?? '',
        customerEmail: customer?.email ?? '',
        customerPhone: customer?.phone ?? '',
        customerAddress: customer?.address ?? '',
        enquiryId: input.enquiryId,
        title: input.title,
        description: input.description,
        lines: input.lines,
        subtotal: totals.subtotal,
        vatAmount: totals.vatAmount,
        total: totals.total,
        status: input.status,
        quoteDate: input.quoteDate,
        validUntil: input.validUntil,
        internalNotes: input.internalNotes,
        customerNotes: input.customerNotes,
        // Teksterne kopieres med på tilbuddet, så de kan redigeres individuelt.
        intro: input.intro ?? defaultQuoteTexts.intro,
        terms: input.terms ?? defaultQuoteTexts.terms,
        closing: input.closing ?? defaultQuoteTexts.closing,
        paymentTerms: input.paymentTerms ?? defaultQuoteTexts.paymentTerms,
        emailStatus: 'ikke sendt',
        history: [{
          id: uid('ev'),
          text: 'Tilbud oprettet',
          at: new Date().toISOString(),
          by: auth.currentUser?.email ?? ''
        }]
      };
      const id = await createQuoteDoc(payload);
      if (input.enquiryId) {
        await updateEnquiryDoc(input.enquiryId, {
          status: 'Behandlet'
        }).catch(() => null);
      }
      const now = new Date().toISOString();
      return {
        ...payload,
        id,
        createdAt: now,
        updatedAt: now
      };
    } catch (error) {
      reportWriteError('createQuote', error);
      return null;
    }
  }, [customers, quotes, reportWriteError]);
  const updateQuote = useCallback(async (id: string, patch: Partial<Quote>): Promise<Result> => {
    try {
      const next = {
        ...patch
      };
      if (patch.lines) {
        const totals = calculateTotals(patch.lines);
        next.subtotal = totals.subtotal;
        next.vatAmount = totals.vatAmount;
        next.total = totals.total;
      }
      // Manuelle gem-handlinger logges i tilbuddets historik.
      if (patch.lines || patch.title !== undefined || patch.description !== undefined) {
        const current = quotes.find(entry => entry.id === id);
        next.history = [...(current?.history ?? []), {
          id: uid('ev'),
          text: 'Tilbud gemt',
          at: new Date().toISOString(),
          by: auth.currentUser?.email ?? ''
        }];
      }
      await updateQuoteDoc(id, next);
      return {
        ok: true
      };
    } catch (error) {
      return {
        ok: false,
        error: reportWriteError('updateQuote', error)
      };
    }
  }, [quotes, reportWriteError]);
  const setQuoteStatus = useCallback(async (id: string, status: QuoteStatus): Promise<Result> => {
    const stamps: Partial<Quote> = {
      status
    };
    const now = new Date().toISOString();
    if (status === 'Sendt') stamps.sentAt = now;
    if (status === 'Accepteret') stamps.acceptedAt = now;
    if (status === 'Afvist') stamps.rejectedAt = now;
    return updateQuote(id, stamps);
  }, [updateQuote]);
  const duplicateQuote = useCallback(async (quote: Quote): Promise<Quote | null> => {
    try {
      const quoteNumber = await reserveQuoteNumber(quotes.map(entry => entry.quoteNumber));
      const payload: Omit<Quote, 'id' | 'createdAt' | 'updatedAt'> = {
        ...quote,
        quoteNumber,
        status: 'Kladde',
        caseId: undefined,
        sentAt: undefined,
        acceptedAt: undefined,
        rejectedAt: undefined,
        emailStatus: 'ikke sendt',
        emailSentAt: undefined,
        quoteDate: today(),
        validUntil: defaultValidUntil(today()),
        title: `${quote.title} (kopi)`
      };
      delete (payload as Partial<Quote>).id;
      const id = await createQuoteDoc(payload);
      const now = new Date().toISOString();
      return {
        ...payload,
        id,
        createdAt: now,
        updatedAt: now
      };
    } catch (error) {
      reportWriteError('duplicateQuote', error);
      return null;
    }
  }, [quotes, reportWriteError]);

  /**
   * Sender tilbuddet som PDF via den sikre backend.
   * Statussen sættes til "Sendt" FØRST når backend bekræfter afsendelsen.
   */
  const sendQuoteByEmail = useCallback(async (quote: Quote, email: {
    to: string;
    subject: string;
    message: string;
  }): Promise<Result> => {
    try {
      const pdfBase64 = await quotePdfBase64(quote, settings);
      const result = await sendQuoteEmail({
        quoteId: quote.id,
        quoteNumber: quote.quoteNumber,
        to: email.to,
        subject: email.subject,
        message: email.message,
        pdfBase64,
        fileName: quoteFileName(quote.quoteNumber)
      });
      if (!result.ok) {
        // Mislykket afsendelse må ikke markere tilbuddet som sendt.
        await updateQuoteDoc(quote.id, {
          emailStatus: 'fejlet'
        }).catch(() => null);
        return {
          ok: false,
          error: result.error ?? 'Tilbuddet kunne ikke sendes. Prøv igen.'
        };
      }
      const now = new Date().toISOString();
      const by = auth.currentUser?.email ?? '';
      await updateQuoteDoc(quote.id, {
        status: 'Sendt',
        sentAt: quote.sentAt ?? now,
        lastSentAt: now,
        sentTo: email.to,
        sentBy: by,
        emailStatus: 'sendt',
        emailSentAt: now,
        history: [...(quote.history ?? []), {
          id: uid('ev'),
          text: `Tilbud sendt til ${email.to}`,
          at: now,
          by
        }]
      });
      return {
        ok: true
      };
    } catch (error) {
      logFirebaseError('sendQuoteByEmail', error);
      await updateQuoteDoc(quote.id, {
        emailStatus: 'fejlet'
      }).catch(() => null);
      const code = firebaseErrorCode(error);
      if (code === 'functions/not-found' || code === 'not-found') {
        return {
          ok: false,
          error: 'Mailfunktionen er endnu ikke udrullet i Firebase. Tilbuddet er gemt, men ikke sendt — download PDF\u2019en i mellemtiden.'
        };
      }
      return {
        ok: false,
        error: debugErrorMessage('sendQuoteByEmail', error)
      };
    }
  }, [settings]);
  const deleteQuote = useCallback(async (id: string): Promise<Result> => {
    try {
      await deleteQuoteDoc(id);
      return {
        ok: true
      };
    } catch (error) {
      return {
        ok: false,
        error: reportWriteError('deleteQuote', error)
      };
    }
  }, [reportWriteError]);
  const createCaseFromQuote = useCallback(async (quote: Quote): Promise<Case | null> => {
    // Er sagen allerede oprettet, returneres den eksisterende i stedet.
    const existing = cases.find(item => item.quoteId === quote.id);
    if (existing) return existing;
    try {
      const payload = {
        number: nextNumber(cases),
        customerId: quote.customerId,
        customerName: quote.customerName,
        title: quote.title,
        address: quote.customerAddress,
        description: quote.description,
        category: 'Multiservice' as const,
        priceModel: 'Fast pris' as const,
        hourlyRate: '',
        fixedPrice: String(Math.round(quote.total)),
        estimate: String(Math.round(quote.total)),
        finalAmount: '',
        plannedDate: '',
        status: 'Planlagt' as CaseStatus,
        internalNotes: quote.internalNotes ? [{
          id: uid('note'),
          text: quote.internalNotes,
          createdAt: new Date().toISOString()
        }] : [],
        photos: [],
        fromWebsite: Boolean(quote.enquiryId),
        enquiryId: quote.enquiryId,
        quoteId: quote.id,
        quoteNumber: quote.quoteNumber,
        quoteLines: quote.lines
      };
      const id = await createCaseDoc(payload);
      await updateQuoteDoc(quote.id, {
        caseId: id
      });
      const now = new Date().toISOString();
      return {
        ...payload,
        id,
        createdAt: now,
        updatedAt: now
      };
    } catch (error) {
      reportWriteError('createCaseFromQuote', error);
      return null;
    }
  }, [cases, reportWriteError]);
  const invoicesForCustomer = useCallback((customerId: string) => invoices.filter(invoice => invoice.customerId === customerId), [invoices]);
  const invoiceForCase = useCallback((caseId: string) => invoices.find(invoice => invoice.caseId === caseId), [invoices]);

  /**
   * Opretter en fakturakladde ud fra sagen. Alt kopieret indhold forbliver
   * redigerbart — fakturaen låses ikke, fordi data kommer fra en sag eller
   * et tilbud. Findes der allerede en faktura på sagen, returneres den.
   */
  const createInvoiceFromCase = useCallback(async (caseItem: Case): Promise<Invoice | null> => {
    const existing = invoices.find(invoice => invoice.caseId === caseItem.id);
    if (existing) return existing;
    try {
      const customer = customers.find(entry => entry.id === caseItem.customerId);
      const quote = quotes.find(entry => entry.id === caseItem.quoteId || entry.caseId === caseItem.id);
      const quoteLines = quote?.lines?.length ? quote.lines : caseItem.quoteLines ?? [];
      const agreed = Number(caseItem.finalAmount || caseItem.fixedPrice || caseItem.estimate) || 0;
      const lines = quoteLines.length ? invoiceLinesFromQuote(quoteLines) : [{
        id: uid('iline'),
        description: caseItem.title || 'Udført arbejde',
        quantity: 1,
        unit: 'fast pris' as const,
        unitPrice: agreed,
        vatRate: 25,
        discount: 0
      }];
      const totals = invoiceTotals(lines);
      const invoiceDate = invoiceToday();
      const invoiceNumber = await reserveInvoiceNumber(invoices.map(entry => entry.invoiceNumber));
      const payload: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'> = {
        invoiceNumber,
        customerId: caseItem.customerId,
        caseId: caseItem.id,
        quoteId: quote?.id,
        customerName: caseItem.customerName || customer?.name || '',
        customerAddress: caseItem.address || customer?.address || '',
        customerPostalCode: '',
        customerCity: '',
        customerEmail: customer?.email ?? '',
        customerPhone: customer?.phone ?? '',
        invoiceDate,
        dueDate: defaultDueDate(invoiceDate),
        // Standardværdierne kopieres ind, men gemmes på fakturaen, så senere
        // ændringer af standarderne ikke rører gamle fakturaer.
        paymentTerms: settings.invoicePaymentTerms || 'Netto 8 dage',
        reference: caseItem.number ? `Sag #${caseItem.number}` : '',
        bankName: settings.invoiceBankName,
        registrationNumber: settings.invoiceRegistrationNumber,
        accountNumber: settings.invoiceAccountNumber,
        paymentReference: invoiceNumber,
        paymentText: settings.invoicePaymentText,
        description: caseItem.description || quote?.description || '',
        lines,
        subtotal: totals.subtotal,
        vatAmount: totals.vatAmount,
        total: totals.total,
        customerNote: settings.invoiceFooterNote || 'Tak for opgaven.',
        internalNote: '',
        status: 'Kladde'
      };
      const id = await createInvoiceDoc(payload);
      await updateCaseDoc(caseItem.id, {
        invoiceId: id,
        invoiceNumber
      }).catch(() => null);
      const now = new Date().toISOString();
      return {
        ...payload,
        id,
        createdAt: now,
        updatedAt: now
      };
    } catch (error) {
      reportWriteError('createInvoiceFromCase', error);
      return null;
    }
  }, [customers, invoices, quotes, reportWriteError, settings]);
  const updateInvoice = useCallback(async (id: string, patch: Partial<Invoice>): Promise<Result> => {
    try {
      const next = {
        ...patch
      };
      if (patch.lines) {
        const totals = invoiceTotals(patch.lines);
        next.subtotal = totals.subtotal;
        next.vatAmount = totals.vatAmount;
        next.total = totals.total;
      }
      await updateInvoiceDoc(id, next);
      return {
        ok: true
      };
    } catch (error) {
      return {
        ok: false,
        error: reportWriteError('updateInvoice', error)
      };
    }
  }, [reportWriteError]);
  const setInvoiceStatus = useCallback(async (id: string, status: InvoiceStatus): Promise<Result> => {
    const stamps: Partial<Invoice> = {
      status
    };
    const now = new Date().toISOString();
    if (status === 'Sendt') stamps.sentAt = now;
    if (status === 'Betalt') stamps.paidAt = now;
    return updateInvoice(id, stamps);
  }, [updateInvoice]);
  const deleteInvoice = useCallback(async (id: string): Promise<Result> => {
    try {
      const invoice = invoices.find(entry => entry.id === id);
      await deleteInvoiceDoc(id);
      if (invoice?.caseId) {
        await updateCaseDoc(invoice.caseId, {
          invoiceId: '',
          invoiceNumber: ''
        }).catch(() => null);
      }
      return {
        ok: true
      };
    } catch (error) {
      return {
        ok: false,
        error: reportWriteError('deleteInvoice', error)
      };
    }
  }, [invoices, reportWriteError]);
  const createQuoteFromEnquiry = useCallback(async (enquiry: Enquiry): Promise<Quote | null> => {
    const existingCustomer = customers.find(customer => enquiry.email && customer.email.toLowerCase() === enquiry.email.toLowerCase() || enquiry.phone && customer.phone.replace(/\s/g, '') === enquiry.phone.replace(/\s/g, ''));
    return createQuote({
      customerId: existingCustomer?.id ?? '',
      newCustomer: existingCustomer ? undefined : {
        name: enquiry.name,
        phone: enquiry.phone,
        email: enquiry.email,
        address: enquiry.address
      },
      enquiryId: enquiry.id,
      title: enquiry.description.split(/[.\n]/)[0].slice(0, 60) || 'Tilbud',
      description: enquiry.description,
      lines: [],
      quoteDate: today(),
      validUntil: defaultValidUntil(today()),
      internalNotes: `Oprettet fra henvendelse på hjemmesiden.`,
      customerNotes: '',
      status: 'Kladde'
    });
  }, [createQuote, customers]);
  const createEnquiry = useCallback(async (input: EnquiryInput): Promise<EnquiryResult> => {
    try {
      const result = await submitEnquiry(input, input.draftId);
      return {
        ok: true,
        draftId: result.draftId
      };
    } catch (error) {
      logFirebaseError('submitEnquiry', error);
      const failure = error as Error & {
        draftId?: string;
        cause?: unknown;
      };
      if (failure?.message === 'upload-failed') {
        // Midlertidig fejlsøgning: den faktiske Storage-kode vises på skærmen.
        const code = firebaseErrorCode(failure.cause) || 'storage/unknown';
        return {
          ok: false,
          draftId: failure.draftId,
          error: `Et eller flere billeder kunne ikke uploades. Fjern billedet eller prøv igen. Din henvendelse er ikke sendt færdig endnu. (${code})`
        };
      }
      if (failure?.message === 'save-failed') {
        const code = firebaseErrorCode(failure.cause) || 'firestore/unknown';
        return {
          ok: false,
          draftId: failure.draftId,
          error: `Henvendelsen kunne ikke gemmes. Prøv igen, eller ring til os i stedet. (${code})`
        };
      }
      return {
        ok: false,
        error: firebaseErrorMessage(error)
      };
    }
  }, []);
  const acceptEnquiry = useCallback(async (id: string): Promise<Result> => {
    try {
      await updateEnquiryDoc(id, {
        status: 'Accepteret'
      });
      return {
        ok: true
      };
    } catch (error) {
      logFirebaseError('acceptEnquiry', error);
      return {
        ok: false,
        error: firebaseErrorMessage(error)
      };
    }
  }, []);
  const deleteEnquiry = useCallback(async (enquiry: Enquiry): Promise<Result> => {
    try {
      await deleteEnquiryDoc(enquiry.id, enquiry.images.map(image => image.path).filter(Boolean));
      return {
        ok: true
      };
    } catch (error) {
      logFirebaseError('deleteEnquiry', error);
      return {
        ok: false,
        error: firebaseErrorMessage(error)
      };
    }
  }, []);
  const createCaseFromEnquiry = useCallback(async (enquiry: Enquiry): Promise<Case | null> => {
    try {
      const existing = customers.find(customer => enquiry.email && customer.email.toLowerCase() === enquiry.email.toLowerCase() || enquiry.phone && customer.phone.replace(/\s/g, '') === enquiry.phone.replace(/\s/g, ''));
      const customerId = existing?.id ?? (await createCustomerDoc({
        name: enquiry.name,
        phone: enquiry.phone,
        email: enquiry.email,
        address: enquiry.address,
        notes: 'Oprettet fra henvendelse på hjemmesiden.'
      }));
      const payload = {
        number: nextNumber(cases),
        customerId,
        customerName: enquiry.name,
        title: enquiry.description.split(/[.\n]/)[0].slice(0, 60) || 'Henvendelse fra hjemmesiden',
        address: enquiry.address,
        description: enquiry.description,
        category: 'Multiservice' as const,
        priceModel: 'Tilbud endnu ikke lavet' as const,
        hourlyRate: '',
        fixedPrice: '',
        estimate: '',
        finalAmount: '',
        plannedDate: '',
        status: 'Ny henvendelse' as CaseStatus,
        internalNotes: [],
        photos: enquiry.images.map(image => ({
          id: uid('photo'),
          stage: 'Før' as PhotoStage,
          ...image
        })),
        fromWebsite: true,
        enquiryId: enquiry.id
      };
      const id = await createCaseDoc(payload);
      await updateEnquiryDoc(enquiry.id, {
        status: 'Behandlet',
        caseId: id
      });
      const now = new Date().toISOString();
      return {
        ...payload,
        id,
        createdAt: now,
        updatedAt: now
      };
    } catch (error) {
      setDataError(firebaseErrorMessage(error));
      return null;
    }
  }, [cases, customers]);
  const value = useMemo<AdminData>(() => ({
    user,
    loggedIn: Boolean(user),
    authLoading,
    login,
    logout,
    changePassword,
    settings,
    settingsLoading,
    saveSettings,
    cases,
    customers,
    enquiries,
    quotes,
    invoices,
    dataLoading,
    dataError,
    lastWriteError: () => writeError.current,
    rulesError: deniedCollections.length ? `Firestore Rules afviser adgang til: ${deniedCollections.join(', ')}. Opdater reglerne for disse samlinger.` : '',
    customerById,
    casesForCustomer,
    quotesForCustomer,
    invoicesForCustomer,
    invoiceForCase,
    createInvoiceFromCase,
    updateInvoice,
    setInvoiceStatus,
    deleteInvoice,
    createQuote,
    updateQuote,
    setQuoteStatus,
    duplicateQuote,
    sendQuoteByEmail,
    deleteQuote,
    createCaseFromQuote,
    createQuoteFromEnquiry,
    createCase,
    updateCase,
    deleteCase,
    setStatus,
    addNote,
    deleteNote,
    addPhotos,
    deletePhoto,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    createEnquiry,
    acceptEnquiry,
    deleteEnquiry,
    createCaseFromEnquiry
  }), [user, authLoading, login, logout, changePassword, settings, settingsLoading, saveSettings, cases, customers, enquiries, quotes, invoices, dataLoading, dataError, deniedCollections, reportWriteError, customerById, casesForCustomer, quotesForCustomer, invoicesForCustomer, invoiceForCase, createInvoiceFromCase, updateInvoice, setInvoiceStatus, deleteInvoice, createQuote, updateQuote, setQuoteStatus, duplicateQuote, sendQuoteByEmail, deleteQuote, createCaseFromQuote, createQuoteFromEnquiry, createCase, updateCase, deleteCase, setStatus, addNote, deleteNote, addPhotos, deletePhoto, createCustomer, updateCustomer, deleteCustomer, createEnquiry, acceptEnquiry, deleteEnquiry, createCaseFromEnquiry]);
  return <AdminDataContext.Provider value={value} data-id="element-499" data-mp="2kd78aony85go">{children}</AdminDataContext.Provider>;
}
export function useAdminData(): AdminData {
  const context = useContext(AdminDataContext);
  if (!context) {
    throw new Error('useAdminData skal bruges inde i AdminDataProvider');
  }
  return context;
}